import React, { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Info, SquarePen } from "lucide-react";
import {
  TooltipProvider,
  TooltipTrigger,
  Tooltip,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { formatEpfNumber, getEmployeeContributionLabel } from "./TaxHelpers";

/* ---------------- Component ---------------- */

/**
 * Renders the EPF configuration form and view.
 * @param {object} props
 * @param {object} props.epfForm - The current EPF form state.
 * @param {function} props.setEpfForm - Function to update EPF form state.
 * @param {object | null} props.epfView - The saved EPF configuration for view mode.
 * @param {boolean} props.isViewMode - Whether to show the view or edit screen.
 * @param {function} props.setViewMode - Function to toggle view/edit mode.
 * @param {function} props.handleSave - Function to save the configuration.
 * @param {function} props.handleCancel - Function to cancel the edit.
 * @param {function} props.handleDisable - Function to disable the configuration.
 * @param {string | null} props.savingSection - The section currently being saved.
 * @returns {JSX.Element}
 */
const EpfConfiguration = ({
  epfForm,
  setEpfForm,
  epfView,
  isViewMode,
  setViewMode,
  handleSave,
  handleCancel,
  handleDisable,
  savingSection,
  canEditTaxConfig,
}) => {
  const [epfError, setEpfError] = useState("");

  const handleEpfNumberChange = useCallback(
    (e) => {
      const formatted = formatEpfNumber(e.target.value);

      setEpfForm((prev) => ({
        ...prev,
        epf_number: formatted,
      }));

      // validation
      const epfRegex = /^[A-Z]\/[A-Z]{3}\/\d{8}\/[A-Z]{3}$/;
      if (formatted.length === 0 || epfRegex.test(formatted)) {
        setEpfError("");
      } else {
        setEpfError("Invalid EPF format");
      }
    },
    [setEpfForm],
  );

  const handleCheckboxChange = useCallback(
    (field) => (checked) => {
      setEpfForm((prev) => ({
        ...prev,
        [field]: !!checked,
      }));
    },
    [setEpfForm],
  );

  const handleSelectChange = useCallback(
    (field) => (value) => {
      setEpfForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [setEpfForm],
  );

  // --- EPF View (Read-Only) ---
  const renderEPFView = () => (
    <div className="grid grid-cols-3 gap-6">
      {/* Left Column - EPF Details */}
      <div className="col-span-2 space-y-6 text-gray-700 dark:text-gray-300">
        <div className="flex gap-3 items-center">
          <span className="font-extrabold text-lg text-black dark:text-white">
            Employees' Provident Fund
          </span>
          <SquarePen
            onClick={() => {
              if (!canEditTaxConfig) return;
              setViewMode((prev) => ({ ...prev, epf: false }));
            }}
            className={`
              p-1 rounded
              ${
                canEditTaxConfig
                  ? "text-gray-600 hover:text-[#3194A0] cursor-pointer"
                  : "text-gray-300 cursor-not-allowed"
              }
            `}
          />
        </div>

        <div className="grid grid-cols-2 gap-y-4">
          <p className="font-semibold">EPF Number</p>
          <p>{epfView?.epf_number || "--"}</p>

          <p className="font-semibold">Deduction Cycle</p>
          <p>{epfView?.deduction_cycle || "--"}</p>

          <p className="font-semibold">Employee Contribution Rate</p>
          <p>
            {getEmployeeContributionLabel(epfView?.employee_contribution_rate)}
          </p>

          <p className="font-semibold">Employer Contribution Rate</p>
          <p>
            {getEmployeeContributionLabel(epfView?.employer_contribution_rate)}
          </p>

          <p className="font-semibold">Employer PF Contribution</p>
          <p>{epfView?.include_employer_contribution ? "Yes" : "No"}</p>

          <p className="font-semibold">EDLI Contribution</p>
          <p>{epfView?.include_edli ? "Yes" : "No"}</p>

          <p className="font-semibold">Admin Charges</p>
          <p>{epfView?.include_admin_charges ? "Yes" : "No"}</p>

          <p className="font-semibold">Allow Employee Override</p>
          <p>{epfView?.override_pf ? "Yes" : "No"}</p>

          <p className="font-semibold">Pro-rate Restricted PF Wage</p>
          <p>{epfView?.pro_rate_pf ? "Yes" : "No"}</p>

          <p className="font-semibold">
            Applicable Components if PF &lt; ₹15,000 (LOP)
          </p>
          <p>
            {epfView?.consider_applicable_if_less_than_15000 ? "Yes" : "No"}
          </p>
        </div>
        {/* Buttons */}
        <div className="flex gap-4 pt-4">
          <Button
            variant="destructive"
            onClick={() => {
              if (!canEditTaxConfig) return;
              handleDisable("epf");
            }}
            disabled={!canEditTaxConfig}
          >
            Disable EPF
          </Button>
        </div>
      </div>

      {/* Right Column - Sample Calculation */}
      {renderSampleCalculation()}
    </div>
  );

  // --- EPF Content (Edit Mode) ---
  const renderEPFContent = () => (
    <div className="grid grid-cols-3 gap-6">
      {/* Left: Form Fields */}
      <div className="col-span-2 space-y-6">
        <div>
          <Label htmlFor="epf_number">EPF Number</Label>
          <Input
            id="epf_number"
            disabled={!canEditTaxConfig}
            placeholder="A/AAA/00000000/XXX"
            className="mt-2"
            value={epfForm.epf_number}
            onChange={handleEpfNumberChange}
          />
          <p className="text-xs text-gray-500 mt-1">
            Format: A/AAA/00000000/XXX
          </p>
          {epfError && <p className="text-xs text-red-600 mt-1">{epfError}</p>}
        </div>

        <div>
          <Label htmlFor="deduction_cycle">
            Deduction Cycle
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="inline w-4 h-4 ml-1 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  Provident Fund (PF) contributions for each month should be
                  deposited to the Employee Provident Fund Organization (EPFO)
                  within the 15th of the following month.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <Select
            value={epfForm.deduction_cycle}
            onValueChange={handleSelectChange("deduction_cycle")}
            disabled={true}
          >
            <SelectTrigger className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="employee_contribution">
            Employee Contribution Rate
          </Label>
          <Select
            disabled={!canEditTaxConfig}
            value={epfForm.employee_contribution_rate}
            onValueChange={handleSelectChange("employee_contribution_rate")}
          >
            <SelectTrigger className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="12_actual">12% of Actual PF Wage</SelectItem>
              <SelectItem value="10_actual">10% of Actual PF Wage</SelectItem>
              <SelectItem value="12_restricted">
                12% of Restricted PF Wage
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex w-full justify-between">
            <Label htmlFor="employer_contribution">
              Employer Contribution Rate
            </Label>
            <Popover>
              <PopoverTrigger className="text-blue-600 underline text-sm cursor-pointer">
                View Splitup
              </PopoverTrigger>

              <PopoverContent
                align="start"
                side="bottom"
                className="w-[420px] p-0 rounded-xl shadow-lg border bg-white"
              >
                {/* Header */}
                <div className="flex justify-between items-center px-4 py-3 border-b">
                  <h3 className="text-sm font-semibold tracking-wide text-gray-500">
                    CONTRIBUTION RATE
                  </h3>
                </div>

                {/* Table */}
                <div className="px-4 py-4">
                  {/* Table Header */}
                  <div className="grid grid-cols-2 pb-2 border-b text-xs font-semibold text-gray-500">
                    <span>SUB COMPONENTS</span>
                    <span className="text-right">EMPLOYER'S CONTRIBUTION</span>
                  </div>

                  {/* Row 1 */}
                  <div className="grid grid-cols-2 py-4 border-b text-sm">
                    <span>Employees' Provident Fund (EPF)</span>
                    <span className="text-right">3.67% of PF Wage</span>
                  </div>

                  {/* Row 2 */}
                  <div className="grid grid-cols-2 py-4 text-sm">
                    <span className="flex items-center">
                      Employees' Pension Scheme
                    </span>
                    <span className="flex items-center justify-end gap-1">
                      8.33% of PF Wage{" "}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="inline w-4 h-4 ml-1 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Maximum Employer Contribution for EPS is ₹1250
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <Select
              disabled={!canEditTaxConfig}
              value={epfForm.employer_contribution_rate}
              onValueChange={handleSelectChange("employer_contribution_rate")}
              className="flex-1"
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="12_actual">12% of Actual PF Wage</SelectItem>
                <SelectItem value="10_actual">10% of Actual PF Wage</SelectItem>
                <SelectItem value="12_restricted">
                  12% of Restricted PF Wage
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="pt-4 border-t">
          <h3 className="font-semibold mb-4 dark:text-stone-50">
            PF Configuration when LOP Applied
          </h3>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                disabled={!canEditTaxConfig}
                id="pro_rate_pf"
                checked={epfForm.pro_rate_pf}
                onCheckedChange={handleCheckboxChange("pro_rate_pf")}
              />
              <Label htmlFor="pro_rate_pf" className="font-normal">
                Pro-rate Restricted PF Wage
              </Label>
            </div>
            <p className="text-sm text-gray-600 ml-6">
              PF contribution will be pro-rated based on the number of days
              worked by the employee.
            </p>

            <div className="flex items-center space-x-2">
              <Checkbox
                disabled={!canEditTaxConfig}
                id="consider_applicable"
                checked={epfForm.consider_applicable_if_less_than_15000}
                onCheckedChange={handleCheckboxChange(
                  "consider_applicable_if_less_than_15000",
                )}
              />
              <Label htmlFor="consider_applicable" className="font-normal">
                Consider all applicable salary components if PF wage is less
                than ₹15,000 after Loss of Pay.
              </Label>
            </div>
            <p className="text-sm text-gray-600 ml-6">
              PF wage will be computed using the salary earned in that
              particular month (based on LOP) rather than the actual amount
              mentioned in the salary structure.
            </p>
          </div>
        </div>

        <div className="flex gap-4 pt-6">
          <Button
            onClick={() => handleSave("epf")}
            disabled={savingSection === "epf" || !!epfError || !canEditTaxConfig}
          >
            {savingSection === "epf" ? "Saving..." : "Save"}
          </Button>
          <Button onClick={handleCancel} variant="outline">
            Cancel
          </Button>
        </div>
      </div>

      {/* Right: Sample Calculation */}
      {renderSampleCalculation()}
    </div>
  );

  // --- Sample Calculation Card (Shared between View and Edit) ---
  const renderSampleCalculation = () => (
    <div>
      <Card className="border-orange-300 border-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sample EPF Calculation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Let's assume the PF wage is ₹20,000. The breakup of contribution
            will be:
          </p>

          <div className="space-y-3 pt-4">
            <div>
              <p className="text-sm font-semibold mb-2">
                Employee's Contribution
              </p>
              <div className="flex justify-between text-sm">
                <span>EPF (12% of 20000)</span>
                <span>₹ 2,400</span>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold mb-2">
                Employer's Contribution
              </p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>EPS (8.33% of 20000, Max of ₹15000)</span>
                  <span>₹ 1,250</span>
                </div>
                <div className="flex justify-between">
                  <span>EPF (3.67 of 20000 - EPS)</span>
                  <span>₹ 1,150</span>
                </div>
              </div>
            </div>

            <div className="pt-3 border-t">
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span>₹ 4,800</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return isViewMode ? renderEPFView() : renderEPFContent();
};

export default EpfConfiguration;
